import { createContext, useEffect, useCallback, useContext, useReducer, useState } from 'react';

import decode from 'jwt-decode';
import { omit } from 'lodash';
import { rawAPI } from '../services/api';
import ToastContext from './toast-context';

const AuthContext = createContext({
  token: '',
  user: undefined,
  isLoggedIn: false,
  isAdmin: false,
  isSuperUser: false,
  logoutNotified: false,
  login: () => {},
  logout: () => {},
});

const calculateRemainingTime = (expirationTime) => {
  const currentTime = new Date().getTime();
  const adjExpirationTime = new Date(expirationTime).getTime();
  const remainingDuration = adjExpirationTime - currentTime;
  return remainingDuration;
};

const retrieveRemainingSessionTime = () => {
  const storedExpirationDate = localStorage.getItem('expirationTime');

  const remainingTime = calculateRemainingTime(storedExpirationDate);

  if (remainingTime <= 30000) {
    localStorage.removeItem('expirationTime');
    return null;
  }

  return remainingTime;
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'login': {
      const decoded = decode(action.payload.token);
      const expirationTime = new Date(action.payload.exp * 1000);
      localStorage.setItem('expirationTime', expirationTime);
      return {
        ...state,
        token: action.payload.token,
        user: omit(decoded, 'iat', 'exp'),
        remainingTime: calculateRemainingTime(expirationTime),
      };
    }
    case 'logout':
    case 'clear-token':
      localStorage.removeItem('expirationTime');
      return { ...state, token: '', user: null, remainingTime: null, logoutNotified: true };

    case 'clear-logout-notified':
      return { ...state, logoutNotified: false };
  }
};

export const AuthProvider = (props) => {
  const { handleError, success, error } = useContext(ToastContext);
  const [initialized, setInitialized] = useState(false);
  const [loginState, dispatch] = useReducer(authReducer, {
    token: '',
    user: null,
    remainingTime: retrieveRemainingSessionTime(),
    logoutNotified: false,
  });

  const logoutHandler = useCallback(
    (sessionExpired = false) => {
      const logout = async () => {
        try {
          await rawAPI.post('/users/logout');
          dispatch({ type: 'logout' });
          if (sessionExpired === true) {
            error('Session expired. Please log in.', {
              onOpen: () => {
                dispatch({ type: 'clear-logout-notified' });
              },
            });
          } else {
            success('Logged out!', {
              onOpen: () => {
                dispatch({ type: 'clear-logout-notified' });
              },
            });
          }
        } catch (err) {
          handleError(err);
        }
      };

      logout();
    },
    [handleError, success, error]
  );

  const loginHandler = useCallback((token, exp) => {
    dispatch({ type: 'login', payload: { token, exp } });
  }, []);

  const clearAccessToken = useCallback(() => {
    dispatch({ type: 'clear-token' });
  }, []);

  // Auto-logout timer
  useEffect(() => {
    if (loginState.remainingTime) {
      const logoutTimer = setTimeout(logoutHandler, loginState.remainingTime, true);
      return () => {
        clearTimeout(logoutTimer);
      };
    }
  }, [loginState.remainingTime, logoutHandler]);

  // Access-token initialization
  useEffect(() => {
    const initSession = async () => {
      const response = await rawAPI.post('/users/accessToken', undefined, {
        validateStatus: null,
      });

      if (response.status === 200) {
        loginHandler(response.headers['x-access-token'], response.data.exp);
      }

      setInitialized(true);
    };

    if (!initialized) {
      initSession();
    }
    // Only want this effect to trigger on initial render, leaving deps array blank.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = {
    token: loginState.token,
    user: loginState.user,
    isLoggedIn: !!loginState.token,
    isAdmin: loginState.user?.role === 'ADMIN' ? true : false,
    logoutNotified: loginState.logoutNotified,
    login: loginHandler,
    logout: logoutHandler,
    // Should only be called to clear an invalid access token once the session token has expired
    clearAccessToken,
  };

  return initialized ? (
    <AuthContext.Provider value={contextValue}>{props.children}</AuthContext.Provider>
  ) : null;
};

export default AuthContext;
